import React, { useCallback, useRef, useState } from "react";
import classes from "./CreateSupportCaseModal.module.scss";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { BaseModal } from "@whatslly/ui-components/modals/BaseModal";
import { CreateSupportCaseForm, StateType } from "@whatslly/ui-components/Pages/CreateSupportCaseForm";
import { fileListToObject } from "@whatslly/commons/fileListToObject";
import type { UploadFileType } from "@whatslly/types/UploadFile";
import { postJSON } from "@whatslly/commons/apiUtils";
import type { CreateSupportCaseResponseType } from "@whatslly/types/integrations/schemas/jira-support-case/CreateSupportCase";
import { Button } from "@whatslly/ui-components/v2/Button";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { useDependencyInjector } from "@whatslly/commons/di";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { parseOrReturnError } from "../../services/utils";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { Text } from "@whatslly/ui-components/Text";
import { ModalHeader, modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";

type ICreateSupportCaseModalProps = IModalInstance;

export const CreateSupportCaseModal: React.FC<ICreateSupportCaseModalProps> = ({ closeModal }) => {
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const logger = useDependencyInjector<FrontLogger>(FrontLogger);
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<StateType>>(null);
  const [allRequiredFieldsExists, setAllRequiredFieldsExists] = useState<boolean>(false);
  const [hasErrorMessage, setHasErrorMessage] = useState(false);

  const onSubmit = useCallback(
    async (stateFields: StateType) => {
      const files = await fileListToObject(stateFields.files);
      const reqBody: Omit<StateType, "files" | "businessImpact" | "emails"> & {
        files: UploadFileType[];
        businessImpact: string;
        platform: string;
        emails: string[];
      } = {
        description: stateFields.description,
        subject: stateFields.subject,
        businessImpact: stateFields.businessImpact.value as string,
        emails: stateFields.emails.split(",").map((item: string) => item.trim()),
        platform: `platform.tuvis.com`,
        files,
      };
      try {
        const response = await postJSON({
          urlSuffix: `/integrations/support-cases`,
          etoken: userStore.etoken ?? "",
          body: JSON.stringify({ ...reqBody, orgId: userStore.user?.organizationId, userId: userStore.user?.userId, userAgent: navigator.userAgent }),
        }).then((r) => parseOrReturnError<CreateSupportCaseResponseType>(r, logger));
        if (!response.success) {
          setHasErrorMessage(true);
          return;
        }
        modalService.openModal(Toast, {
          text: t("support_case.create_support_case_successfully"),
        });
        setHasErrorMessage(false);
        closeModal();
      } catch {
        setHasErrorMessage(true);
      }
    },
    [closeModal, logger, t, userStore.etoken, userStore.user?.organizationId, userStore.user?.userId]
  );

  return (
    <BaseModal
      header={<ModalHeader closeModal={closeModal}>{t("support_case.title")}</ModalHeader>}
      closeModal={() => false}
      className={classes.modal}
      bodyClassName={classes.body}
    >
      <div className={classes.container}>
        <CreateSupportCaseForm formRef={formRef} onSubmit={onSubmit} setAllRequiredFieldsExists={setAllRequiredFieldsExists} />
        {hasErrorMessage && (
          <Text testId={`support_case_error`} skin="error" className={classes.errorMessage}>
            {t("support_case.failed_to_create_support_case")}
          </Text>
        )}
        <div className={classes.submit}>
          <Button
            disabled={!allRequiredFieldsExists}
            text={t("submit")}
            testId={`support_case_submit`}
            onClick={async () => {
              await formRef.current?.validateForm();
              formRef.current?.submitForm();
            }}
          />
        </div>
      </div>
    </BaseModal>
  );
};
